import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSEO } from '@/hooks/useSEO';
import SocratDemoVideo from '@/components/SocratDemoVideo';
import BookDemoModal from '@/components/BookDemoModal';
import SampleReportModal from '@/components/SampleReportModal';
import { supabase } from '@/integrations/supabase/client';
import confetti from 'canvas-confetti';
import {
  Satellite, Globe, Zap, Clock, ChevronDown, ChevronUp, X, Menu,
  ArrowRight, Check, Send, BarChart3, Cpu, Radar, Layers, Database,
  Pickaxe, Fuel, Wheat, Building2, TreePine, Shield, Activity, Radio,
  Linkedin, Mail, MapPin
} from 'lucide-react';

// ─── COUNTRIES LIST ───
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Angola","Argentina","Australia","Austria","Azerbaijan",
  "Bahrain","Bangladesh","Belgium","Bolivia","Botswana","Brazil","Burkina Faso","Cameroon",
  "Canada","Chad","Chile","China","Colombia","Congo (DRC)","Côte d'Ivoire","Czech Republic",
  "Denmark","Ecuador","Egypt","Ethiopia","Finland","France","Gabon","Germany","Ghana","Greece",
  "Guatemala","Guinea","Honduras","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Kyrgyzstan","Lebanon","Libya",
  "Madagascar","Malawi","Malaysia","Mali","Mauritania","Mexico","Mongolia","Morocco","Mozambique",
  "Myanmar","Namibia","Netherlands","New Zealand","Niger","Nigeria","Norway","Oman","Pakistan",
  "Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Sierra Leone","Singapore","South Africa","South Korea",
  "Spain","Sri Lanka","Sudan","Sweden","Switzerland","Tanzania","Thailand","Tunisia","Turkey",
  "UAE","Uganda","UK","Ukraine","USA","Uruguay","Uzbekistan","Venezuela","Vietnam","Zambia","Zimbabwe"
];

// ─── TRANSLATIONS ───
type Lang = 'en' | 'fr' | 'es';
const T = {
  en: {
    nav: { solutions: 'SOCRAT', how: 'How It Works', cases: 'Use Cases', pricing: 'Pricing', contact: 'Contact', cta: 'Brief SOCRAT →' },
    hero: {
      h1a: 'SOCRAT — The only AI agent for ', h1b: 'natural-resource discovery',
      sub1a: 'Mining · Oil & Gas · Lithium & Rare Earths · Hydro Security — ', sub1b: 'satellite + IoT + geophysics, fused.',
      sub2: 'SOCRAT is a sovereign AI agent with live access to multi-spectral & SAR satellite constellations, ground IoT, geophysical surveys and historic drill logs. It scores every km² before a single drill is funded — turning speculative exploration into predictive intelligence.',
      cta1: 'Request a Sovereign Briefing', cta2: 'See a Sample Report',
      b1: 'Multi-spectral + SAR satellite fusion', b2: 'IoT, geophysics & drill-log ingestion', b3: 'Drill-ready intelligence in days',
    },
    socrat: {
      tag: 'INTRODUCING SOCRAT',
      title: 'The Sovereign Resource Intelligence Agent',
      sub: 'One autonomous AI agent. Seven data layers. Continental-scale coverage. Built for ministries, majors and capital that cannot afford a dry hole.',
      stats: [
        { v: '−70%', l: 'Exploration CAPEX' },
        { v: '10×', l: 'Faster time to drill' },
        { v: '4.2×', l: 'Fewer dry drillings' },
        { v: '12,400', l: 'km² per scan campaign' },
      ],
      caps: [
        { t: 'Satellite Access', d: 'Live multi-spectral, hyperspectral, SAR & thermal feeds across global constellations.' },
        { t: 'IoT & Sensor Mesh', d: 'Ingests ground stations, hydric sensors, drone telemetry and edge devices in real time.' },
        { t: 'Geophysical Fusion', d: 'Magnetics, gravity, radiometrics and ERT layered with structural geology models.' },
        { t: 'Sovereign ML Core', d: 'Onboards your historic surveys & drill logs into a private, jurisdiction-hosted model.' },
      ],
    },
    why: {
      title: 'Why ', titleAccent: 'SOCRAT', q: '',
      old: 'Speculative Exploration', new: 'Predictive Intelligence',
      olds: ['Weeks or months of on-the-ground surveys', 'Millions spent on exploratory drilling in the dark', 'Data hidden behind complex GIS software', "No results until you've already committed capital"],
      news: ['Pre-CAPEX scoring of every km² before drilling', 'Capital flows where probability is provable', 'Executive-ready intelligence — no GIS required', 'One avoided dry campaign finances the platform'],
    },
    how: {
      title: 'How ', titleAccent: 'SOCRAT', t2: ' Works',
      step: 'STEP', cta: 'Brief SOCRAT on Your Perimeter',
      steps: [
        { t: 'BRIEF', d: 'Define your concession, basin or sovereign perimeter. 3 minutes online.' },
        { t: 'FUSE', d: 'SOCRAT fuses multi-spectral, SAR, geophysical and IoT signals through its sovereign ML core.' },
        { t: 'DECIDE', d: 'Receive scored, ranked, drill-ready intelligence — board-grade, in days not years.' },
      ],
    },
    cases: {
      title: 'Where SOCRAT ', titleAccent: 'Operates',
      sub: 'Designed for capital at risk in the world\'s most contested geologies — gold, copper, lithium, hydrocarbons and water.',
      items: [
        { i: 'pickaxe', t: 'Gold · Copper · Iron', p: ['Structural convergence detection across 12 km corridors', '3D drill-target ranking with confidence scoring', 'For majors, mid-tier producers & juniors'] },
        { i: 'cpu', t: 'Lithium & Rare Earths', p: ['Pegmatite vectorisation from hyperspectral signatures', 'REE potential mapping for sovereign critical-mineral strategies', 'Co · Ni · Li portfolio screening'] },
        { i: 'fuel', t: 'Oil & Gas', p: ['Surface hydrocarbon seepage detection', 'Geological fault & trap structure mapping', 'Cut prospecting timelines from months to days'] },
        { i: 'tree', t: 'Hydro & Water Security', p: ['National hydric mapping for ministries & basin authorities', 'Aquifer detection & drought-risk modelling', 'Secure social licence before extraction'] },
        { i: 'wheat', t: 'Agricultural Intelligence', p: ['Predict yields weeks before harvest', 'Detect water stress & disease signatures early', 'Optimise inputs zone by zone'] },
        { i: 'shield', t: 'Sovereign Land Monitoring', p: ['Detect illegal extraction or encroachment', 'Quarterly re-scoring of active assets', 'Drift, depletion & leakage alerts'] },
      ],
    },
    stats: { labels: ['km² Scanned', 'Countries Covered', 'Avg. Delivery', 'Capital De-Risked'] },
    services: {
      title: 'Strategic Intelligence ', titleAccent: 'Services',
      sub: 'Per-km² engagements when you need decisive answers on a defined perimeter, fast. All prices starting from — scoped per project.',
      from: 'Starting from',
      cards: [
        { t: 'Hydro Audit', d: 'Secure social licence by mapping water before extraction.', delivery: '10–14 days', rigor: 'Tactical', value: 'Risk · Social Licence', price: '€500 / km²', cta: 'Scope Hydro Audit →' },
        { t: 'Multi-Layer Resource Scan', d: 'Hyperspectral + SAR fusion across gold, lithium, copper signatures.', delivery: '15–25 days', rigor: 'Strategic', value: 'Discovery acceleration', price: '€2,000 / km²', cta: 'Scope Resource Scan →' },
        { t: 'Advanced Mining Intelligence', d: 'Pegmatite vectorisation, structural convergence & 3D drill-target ranking.', delivery: '20–35 days', rigor: 'Premium', value: 'CAPEX optimisation', price: '€5,000 / km²', cta: 'Scope Mining Intelligence →' },
        { t: 'Continuous Monitoring', d: 'Quarterly re-scoring of active assets — drift, depletion, leakage, encroachment.', delivery: '90-day cycle', rigor: 'Continuous', value: 'Asset protection', price: '€120k / year', cta: 'Scope Monitoring →' },
        { t: 'Private Data Fusion', d: 'Onboard your historic surveys, drill logs & geophysics into a sovereign ML core.', delivery: 'On engagement', rigor: 'Sovereign', value: 'Sovereign IP leverage', price: '€50k / project', cta: 'Scope Private Fusion →' },
      ],
      ref: 'One avoided dry-drilling campaign typically finances 6–10 of the most extensive scans above.',
    },
    pricing: {
      title: 'Platform ', titleAccent: 'Access',
      sub: 'Subscription tiers calibrated to capital at risk. Each tier benchmarked against the cost of a single failed exploration round.',
      popular: 'Most Popular',
      from: 'Starting from',
      cards: [
        { t: 'STARTUP EXPLORATION', d: 'Junior miners · early-stage explorers', price: '€48k / year', note: '≈ 1/10 of a single dry drilling', f: ['Up to 500 km² scanning', '3 commodity layers', 'Pre-permit zone screening', 'Hydro & social licence audits', 'Avoid 1 dry hole → ROI > 8×'], cta: 'Request Access →' },
        { t: 'REGIONAL OPERATOR', d: 'Mid-tier producers · regional concessions', price: '€220k / year', note: 'Pays for itself on 1 avoided campaign', f: ['Up to 5,000 km²', 'All commodity layers', 'API access', 'Investor-grade discovery report', 'Asset monitoring & alerts', '−68% exploration CAPEX typical'], cta: 'Request Access →' },
        { t: 'GLOBAL MINING GROUP', d: 'Majors · multi-jurisdiction portfolios', price: '€950k / year', note: 'vs €5–15M / failed CAPEX round', f: ['Unlimited km²', 'Private model fine-tuning', 'Quarterly re-scoring', 'Global portfolio re-ranking', 'M&A asset due diligence', 'Reallocates 9-figure CAPEX'], cta: 'Book Strategic Briefing →' },
        { t: 'GOVERNMENT / SOVEREIGN', d: 'Ministries · sovereign wealth · defence', price: '€2.5M+ / engagement', note: 'National-scale resource census', f: ['National-scale coverage', 'Sovereign cloud · full IP', 'Concession-wide drill priority', 'Geopolitical risk overlays', 'National critical-minerals census', 'Strategic water security mapping'], cta: 'Request Sovereign Briefing →' },
      ],
      footer: 'One avoided dry drilling campaign can finance the entire platform — for years.',
    },
    form: {
      received: 'Briefing Received!',
      receivedDesc: 'Our intelligence team will return scored, ranked, drill-ready intelligence within weeks. A confirmation has been sent to',
      title: 'Request a Sovereign Briefing',
      sub: 'Brief SOCRAT on your concession, basin or sovereign perimeter. Confidential. NDA available.',
      step1: 'Step 1 — Perimeter & Contact', step2: 'Step 2 — Mission & Context',
      fullName: 'Full Name', company: 'Company', email: 'Professional Email', phone: 'Phone / WhatsApp',
      country: 'Country', selectCountry: 'Select country...',
      zoneLoc: 'Perimeter Description', zoneLocPh: 'e.g. Northern Mali, 20km east of Kidal — or paste GPS coordinates',
      zoneSize: 'Approximate Perimeter Size',
      next: 'Next Step', back: '← Back',
      analysisType: 'Mission Type',
      analysisTypes: ['Gold · Copper · Iron Exploration', 'Lithium & Rare Earths', 'Oil & Gas Prospecting', 'Hydro & Water Security', 'Agricultural Intelligence', 'Sovereign Land Monitoring'],
      reportType: 'Engagement Tier',
      reportTypes: ['Hydro Audit (from €500/km²)', 'Multi-Layer Resource Scan (from €2,000/km²)', 'Advanced Mining Intelligence (from €5,000/km²)', 'Continuous Monitoring (from €120k/year)', 'Platform Access (from €48k/year)', 'Sovereign Briefing'],
      urgency: 'Project Urgency',
      urgencyOpts: { standard: 'Standard', urgent: 'Urgent (< 48H)', critical: 'Critical — Board Decision Imminent' },
      context: 'Additional Context',
      nda: 'Yes, I require a Non-Disclosure Agreement',
      found: 'How did you find us?', selectOpt: 'Select...',
      submit: 'Submit Briefing →', submitting: 'Submitting...',
    },
    faq: {
      title: 'Frequently Asked ', titleAccent: 'Questions',
      items: [
        { q: 'What makes SOCRAT different from other satellite or GIS providers?', a: 'SOCRAT is not a satellite reseller or a GIS dashboard — it is the only autonomous AI agent purpose-built for natural-resource discovery. It has live access to multi-spectral, SAR, hyperspectral and thermal satellite constellations, ground IoT and geophysical sensors, and ingests your historic drill logs into a sovereign ML core. The output is decision-grade probability scoring, not raw imagery.' },
        { q: 'How accurate are SOCRAT\'s exploration intelligence reports?', a: 'Reports achieve 85–95% confidence depending on commodity and zone conditions. We fuse multi-spectral imaging down to 30 cm resolution with SAR, thermal, magnetics, gravity and radiometrics, validated against historic drill data.' },
        { q: 'Can satellite analysis really replace geological field surveys?', a: 'No — SOCRAT is a force multiplier. By pre-scoring every km² from space and IoT, you focus expensive field surveys and drilling only on the highest-probability targets, typically reducing exploration CAPEX by 60–80%.' },
        { q: 'Which natural resources can SOCRAT detect?', a: 'Gold, copper, iron, zinc, lithium, rare earths, cobalt, nickel, oil & gas seepage, groundwater & aquifers, soil composition, vegetation health and crop yield signatures.' },
        { q: 'Is my exploration data kept confidential?', a: 'Absolutely. All perimeter data is encrypted in transit and at rest. NDAs are standard for Multi-Layer Resource Scans and above. Sovereign tier deployments run on jurisdiction-hosted infrastructure with full IP transfer.' },
        { q: 'Do you cover all countries and regions?', a: 'Yes — SOCRAT operates globally. From a 50 km² screening to a national 5,000+ km² critical-minerals census, we scale with your perimeter.' },
      ],
    },
    footer: { tagline: 'Stop funding drills. Start funding answers.', solutions: 'Solutions', company: 'Company', contact: 'Contact', rights: '© 2026 Sovereign Scout · Quantumbox DeepTech Lab. All rights reserved.' },
  },
  fr: {
    nav: { solutions: 'SOCRAT', how: 'Fonctionnement', cases: 'Cas d\'usage', pricing: 'Tarifs', contact: 'Contact', cta: 'Briefer SOCRAT →' },
    hero: {
      h1a: 'SOCRAT — Le seul agent IA pour ', h1b: 'la découverte de ressources naturelles',
      sub1a: 'Mines · Pétrole & Gaz · Lithium & Terres Rares · Sécurité Hydrique — ', sub1b: 'satellite + IoT + géophysique, fusionnés.',
      sub2: 'SOCRAT est un agent IA souverain avec accès en temps réel aux constellations satellites multi-spectrales et SAR, à l\'IoT terrestre, aux relevés géophysiques et aux historiques de forage. Il score chaque km² avant qu\'un seul forage ne soit financé — transformant l\'exploration spéculative en intelligence prédictive.',
      cta1: 'Demander un briefing souverain', cta2: 'Voir un rapport exemple',
      b1: 'Fusion satellite multi-spectrale + SAR', b2: 'IoT, géophysique & historiques de forage', b3: 'Intelligence drill-ready en quelques jours',
    },
    socrat: {
      tag: 'DÉCOUVREZ SOCRAT',
      title: 'L\'agent souverain d\'intelligence des ressources',
      sub: 'Un agent IA autonome. Sept couches de données. Couverture continentale. Conçu pour les ministères, les majors et les capitaux qui ne peuvent pas se permettre un forage sec.',
      stats: [
        { v: '−70%', l: 'CAPEX d\'exploration' },
        { v: '10×', l: 'Délai jusqu\'au forage' },
        { v: '4.2×', l: 'Forages secs en moins' },
        { v: '12 400', l: 'km² par campagne' },
      ],
      caps: [
        { t: 'Accès satellite', d: 'Flux multi-spectraux, hyperspectraux, SAR & thermiques en direct sur les constellations mondiales.' },
        { t: 'Maillage IoT & capteurs', d: 'Stations au sol, capteurs hydriques, télémétrie drone et edge devices ingérés en temps réel.' },
        { t: 'Fusion géophysique', d: 'Magnétiques, gravité, radiométriques et ERT superposés aux modèles structuraux.' },
        { t: 'Cœur ML souverain', d: 'Vos relevés et logs de forage historiques intégrés à un modèle privé hébergé en juridiction.' },
      ],
    },
    why: {
      title: 'Pourquoi ', titleAccent: 'SOCRAT', q: '',
      old: 'Exploration spéculative', new: 'Intelligence prédictive',
      olds: ['Des semaines ou mois de relevés sur le terrain', 'Des millions dépensés en forages exploratoires à l\'aveugle', 'Données enfermées dans des logiciels SIG complexes', 'Aucun résultat avant d\'avoir engagé du capital'],
      news: ['Scoring de chaque km² avant tout forage', 'Le capital flue où la probabilité est prouvable', 'Intelligence prête pour le board — sans SIG', 'Une campagne sèche évitée finance la plateforme'],
    },
    how: {
      title: 'Fonctionnement de ', titleAccent: 'SOCRAT', t2: '',
      step: 'ÉTAPE', cta: 'Briefer SOCRAT sur votre périmètre',
      steps: [
        { t: 'BRIEFER', d: 'Définissez votre concession, bassin ou périmètre souverain. 3 minutes en ligne.' },
        { t: 'FUSIONNER', d: 'SOCRAT fusionne les signaux multi-spectraux, SAR, géophysiques et IoT via son cœur ML souverain.' },
        { t: 'DÉCIDER', d: 'Recevez une intelligence scorée, classée, drill-ready — qualité board, en jours et non en années.' },
      ],
    },
    cases: {
      title: 'Où SOCRAT ', titleAccent: 'opère',
      sub: 'Conçu pour les capitaux à risque dans les géologies les plus contestées — or, cuivre, lithium, hydrocarbures et eau.',
      items: [
        { i: 'pickaxe', t: 'Or · Cuivre · Fer', p: ['Détection de convergence structurale sur 12 km', 'Classement de cibles de forage 3D avec score de confiance', 'Pour majors, mid-tier et juniors'] },
        { i: 'cpu', t: 'Lithium & Terres rares', p: ['Vectorisation de pegmatites depuis signatures hyperspectrales', 'Cartographie de potentiel REE pour stratégies critiques souveraines', 'Screening Co · Ni · Li'] },
        { i: 'fuel', t: 'Pétrole & Gaz', p: ['Détection de suintements d\'hydrocarbures', 'Cartographie failles & pièges structuraux', 'Réduire les délais de prospection de mois à jours'] },
        { i: 'tree', t: 'Hydro & Sécurité de l\'eau', p: ['Cartographie hydrique nationale pour ministères', 'Détection d\'aquifères & modélisation du risque sécheresse', 'Sécuriser la licence sociale avant extraction'] },
        { i: 'wheat', t: 'Intelligence agricole', p: ['Prédire rendements des semaines avant récolte', 'Détecter stress hydrique & maladies tôt', 'Optimiser les intrants zone par zone'] },
        { i: 'shield', t: 'Surveillance souveraine', p: ['Détecter extraction illégale ou empiètement', 'Re-scoring trimestriel d\'actifs actifs', 'Alertes drift, dépôt & fuite'] },
      ],
    },
    stats: { labels: ['km² scannés', 'Pays couverts', 'Délai moyen', 'Capital dé-risqué'] },
    services: {
      title: 'Services ', titleAccent: 'd\'intelligence stratégique',
      sub: 'Engagements au km² pour des réponses décisives sur un périmètre défini, rapidement. Tous les prix à partir de — cadré par projet.',
      from: 'À partir de',
      cards: [
        { t: 'Audit Hydro', d: 'Sécuriser la licence sociale en cartographiant l\'eau avant extraction.', delivery: '10–14 jours', rigor: 'Tactique', value: 'Risque · Licence sociale', price: '€500 / km²', cta: 'Cadrer Audit Hydro →' },
        { t: 'Scan multicouche', d: 'Fusion hyperspectrale + SAR sur signatures or, lithium, cuivre.', delivery: '15–25 jours', rigor: 'Stratégique', value: 'Accélération de découverte', price: '€2 000 / km²', cta: 'Cadrer Resource Scan →' },
        { t: 'Mining Intelligence avancée', d: 'Vectorisation pegmatite, convergence structurale & ranking 3D.', delivery: '20–35 jours', rigor: 'Premium', value: 'Optimisation CAPEX', price: '€5 000 / km²', cta: 'Cadrer Mining Intelligence →' },
        { t: 'Surveillance continue', d: 'Re-scoring trimestriel d\'actifs — drift, fuite, empiètement.', delivery: 'Cycle 90 j', rigor: 'Continu', value: 'Protection d\'actifs', price: '€120k / an', cta: 'Cadrer Surveillance →' },
        { t: 'Fusion de données privée', d: 'Intégrez vos relevés et logs historiques dans un cœur ML souverain.', delivery: 'Sur engagement', rigor: 'Souverain', value: 'Levier IP souverain', price: '€50k / projet', cta: 'Cadrer Fusion privée →' },
      ],
      ref: 'Une seule campagne de forage sèche évitée finance typiquement 6–10 des scans les plus extensifs ci-dessus.',
    },
    pricing: {
      title: 'Accès ', titleAccent: 'à la plateforme',
      sub: 'Abonnements calibrés sur le capital à risque. Chaque tier benchmarké contre le coût d\'une seule campagne échouée.',
      popular: 'Le plus populaire',
      from: 'À partir de',
      cards: [
        { t: 'STARTUP EXPLORATION', d: 'Juniors · explorateurs early-stage', price: '€48k / an', note: '≈ 1/10 d\'un seul forage sec', f: ['Jusqu\'à 500 km² scannés', '3 couches de commodités', 'Screening pré-permis', 'Audits hydro & licence sociale', 'Éviter 1 forage sec → ROI > 8×'], cta: 'Demander accès →' },
        { t: 'OPÉRATEUR RÉGIONAL', d: 'Producteurs mid-tier · concessions régionales', price: '€220k / an', note: 'Rentabilisé sur 1 campagne évitée', f: ['Jusqu\'à 5 000 km²', 'Toutes commodités', 'Accès API', 'Rapport découverte investisseur', 'Surveillance & alertes', '−68% CAPEX exploration typique'], cta: 'Demander accès →' },
        { t: 'GROUPE MINIER GLOBAL', d: 'Majors · portefeuilles multi-juridictions', price: '€950k / an', note: 'vs €5–15M / campagne échouée', f: ['km² illimités', 'Fine-tuning modèle privé', 'Re-scoring trimestriel', 'Re-classement portefeuille global', 'Due diligence M&A', 'Réalloue CAPEX 9 chiffres'], cta: 'Réserver briefing →' },
        { t: 'GOUVERNEMENT / SOUVERAIN', d: 'Ministères · fonds souverains · défense', price: '€2,5M+ / engagement', note: 'Recensement national', f: ['Couverture nationale', 'Cloud souverain · IP complète', 'Priorité forage par concession', 'Overlays risques géopolitiques', 'Recensement minéraux critiques', 'Cartographie sécurité hydrique'], cta: 'Demander briefing souverain →' },
      ],
      footer: 'Une seule campagne de forage sèche évitée peut financer toute la plateforme — pour des années.',
    },
    form: {
      received: 'Briefing reçu !',
      receivedDesc: 'Notre équipe d\'intelligence vous renverra une intelligence scorée, classée et drill-ready en quelques semaines. Confirmation envoyée à',
      title: 'Demander un briefing souverain',
      sub: 'Briefez SOCRAT sur votre concession, bassin ou périmètre souverain. Confidentiel. NDA disponible.',
      step1: 'Étape 1 — Périmètre & Contact', step2: 'Étape 2 — Mission & Contexte',
      fullName: 'Nom complet', company: 'Entreprise', email: 'Email professionnel', phone: 'Téléphone / WhatsApp',
      country: 'Pays', selectCountry: 'Sélectionner un pays...',
      zoneLoc: 'Description du périmètre', zoneLocPh: 'ex. Nord Mali, 20km à l\'est de Kidal — ou collez les coordonnées GPS',
      zoneSize: 'Taille approximative du périmètre',
      next: 'Étape suivante', back: '← Retour',
      analysisType: 'Type de mission',
      analysisTypes: ['Or · Cuivre · Fer', 'Lithium & Terres rares', 'Pétrole & Gaz', 'Hydro & Sécurité de l\'eau', 'Intelligence agricole', 'Surveillance souveraine'],
      reportType: 'Niveau d\'engagement',
      reportTypes: ['Audit Hydro (dès €500/km²)', 'Scan multicouche (dès €2 000/km²)', 'Mining Intelligence (dès €5 000/km²)', 'Surveillance continue (dès €120k/an)', 'Accès plateforme (dès €48k/an)', 'Briefing souverain'],
      urgency: 'Urgence du projet',
      urgencyOpts: { standard: 'Standard', urgent: 'Urgent (< 48H)', critical: 'Critique — Décision imminente' },
      context: 'Contexte additionnel',
      nda: 'Oui, j\'ai besoin d\'un accord de confidentialité (NDA)',
      found: 'Comment nous avez-vous trouvés ?', selectOpt: 'Sélectionner...',
      submit: 'Envoyer le briefing →', submitting: 'Envoi...',
    },
    faq: {
      title: 'Questions ', titleAccent: 'fréquentes',
      items: [
        { q: 'En quoi SOCRAT diffère-t-il des autres fournisseurs satellite ou SIG ?', a: 'SOCRAT n\'est pas un revendeur d\'imagerie ni un dashboard SIG — c\'est le seul agent IA autonome dédié à la découverte de ressources naturelles. Il a accès en direct aux constellations multi-spectrales, SAR, hyperspectrales et thermiques, à l\'IoT terrestre et aux capteurs géophysiques, et ingère vos historiques de forage dans un cœur ML souverain.' },
        { q: 'Quelle est la précision de SOCRAT ?', a: 'Niveau de confiance 85–95% selon la commodité et la zone. Fusion multi-spectrale jusqu\'à 30 cm + SAR, thermique, magnétiques, gravité et radiométriques, validée contre données de forage historiques.' },
        { q: 'L\'analyse satellite peut-elle remplacer les relevés terrain ?', a: 'Non — SOCRAT est un multiplicateur de force. En pré-scorant chaque km² depuis l\'espace et l\'IoT, vous concentrez relevés terrain et forages sur les cibles à plus haute probabilité, réduisant typiquement le CAPEX de 60–80%.' },
        { q: 'Quelles ressources SOCRAT détecte-t-il ?', a: 'Or, cuivre, fer, zinc, lithium, terres rares, cobalt, nickel, suintements pétrole/gaz, eau souterraine, composition des sols, santé des cultures.' },
        { q: 'Mes données d\'exploration sont-elles confidentielles ?', a: 'Absolument. Données chiffrées en transit et au repos. NDA par défaut à partir du Scan multicouche. Tier souverain hébergé en juridiction avec transfert IP complet.' },
        { q: 'Couvrez-vous tous les pays ?', a: 'Oui — SOCRAT opère mondialement. Du screening de 50 km² au recensement national de 5 000+ km², nous nous adaptons au périmètre.' },
      ],
    },
    footer: { tagline: 'Arrêtez de financer des forages. Financez des réponses.', solutions: 'Solutions', company: 'Société', contact: 'Contact', rights: '© 2026 Sovereign Scout · Quantumbox DeepTech Lab. Tous droits réservés.' },
  },
  es: {
    nav: { solutions: 'SOCRAT', how: 'Cómo funciona', cases: 'Casos', pricing: 'Precios', contact: 'Contacto', cta: 'Informar a SOCRAT →' },
    hero: {
      h1a: 'SOCRAT — El único agente IA para ', h1b: 'el descubrimiento de recursos naturales',
      sub1a: 'Minería · Petróleo y Gas · Litio y Tierras Raras · Seguridad Hídrica — ', sub1b: 'satélite + IoT + geofísica, fusionados.',
      sub2: 'SOCRAT es un agente IA soberano con acceso en vivo a constelaciones satelitales multiespectrales y SAR, IoT terrestre, estudios geofísicos e historiales de perforación. Puntúa cada km² antes de que se financie una sola perforación — convirtiendo la exploración especulativa en inteligencia predictiva.',
      cta1: 'Solicitar briefing soberano', cta2: 'Ver informe de muestra',
      b1: 'Fusión satelital multiespectral + SAR', b2: 'IoT, geofísica e historiales de perforación', b3: 'Inteligencia drill-ready en días',
    },
    socrat: {
      tag: 'PRESENTAMOS SOCRAT',
      title: 'El agente soberano de inteligencia de recursos',
      sub: 'Un agente IA autónomo. Siete capas de datos. Cobertura continental. Construido para ministerios, majors y capital que no puede permitirse un pozo seco.',
      stats: [
        { v: '−70%', l: 'CAPEX de exploración' },
        { v: '10×', l: 'Tiempo a perforación' },
        { v: '4.2×', l: 'Menos pozos secos' },
        { v: '12 400', l: 'km² por campaña' },
      ],
      caps: [
        { t: 'Acceso satelital', d: 'Multiespectral, hiperespectral, SAR y térmico en vivo sobre constelaciones globales.' },
        { t: 'Malla IoT', d: 'Estaciones terrestres, sensores hídricos, telemetría de drones y edge en tiempo real.' },
        { t: 'Fusión geofísica', d: 'Magnetometría, gravedad, radiometría y ERT con modelos estructurales.' },
        { t: 'Núcleo ML soberano', d: 'Sus estudios y logs históricos en un modelo privado alojado en jurisdicción.' },
      ],
    },
    why: {
      title: 'Por qué ', titleAccent: 'SOCRAT', q: '',
      old: 'Exploración especulativa', new: 'Inteligencia predictiva',
      olds: ['Semanas o meses de estudios de campo', 'Millones gastados en perforaciones a ciegas', 'Datos atrapados en software GIS complejo', 'Sin resultados hasta haber comprometido capital'],
      news: ['Puntuación pre-CAPEX de cada km²', 'El capital fluye donde la probabilidad es demostrable', 'Inteligencia ejecutiva — sin GIS', 'Una campaña seca evitada financia la plataforma'],
    },
    how: {
      title: 'Cómo funciona ', titleAccent: 'SOCRAT', t2: '',
      step: 'PASO', cta: 'Informar a SOCRAT sobre su perímetro',
      steps: [
        { t: 'INFORMAR', d: 'Defina su concesión, cuenca o perímetro soberano. 3 minutos en línea.' },
        { t: 'FUSIONAR', d: 'SOCRAT fusiona señales multiespectrales, SAR, geofísicas e IoT a través de su núcleo ML soberano.' },
        { t: 'DECIDIR', d: 'Reciba inteligencia puntuada, clasificada y drill-ready — calidad directorio, en días, no años.' },
      ],
    },
    cases: {
      title: 'Dónde opera ', titleAccent: 'SOCRAT',
      sub: 'Diseñado para capital en riesgo en las geologías más disputadas — oro, cobre, litio, hidrocarburos y agua.',
      items: [
        { i: 'pickaxe', t: 'Oro · Cobre · Hierro', p: ['Detección de convergencia estructural en 12 km', 'Ranking 3D de objetivos de perforación', 'Para majors, mid-tier y juniors'] },
        { i: 'cpu', t: 'Litio y Tierras raras', p: ['Vectorización de pegmatitas desde firmas hiperespectrales', 'Mapeo de potencial REE para estrategias críticas soberanas', 'Screening Co · Ni · Li'] },
        { i: 'fuel', t: 'Petróleo y Gas', p: ['Detección de filtraciones de hidrocarburos', 'Mapeo de fallas y trampas estructurales', 'Reducir tiempos de prospección de meses a días'] },
        { i: 'tree', t: 'Hidro y Seguridad del agua', p: ['Mapeo hídrico nacional para ministerios', 'Detección de acuíferos y riesgo de sequía', 'Asegurar licencia social antes de extraer'] },
        { i: 'wheat', t: 'Inteligencia agrícola', p: ['Predecir rendimientos semanas antes de cosecha', 'Detectar estrés hídrico y enfermedades temprano', 'Optimizar insumos zona por zona'] },
        { i: 'shield', t: 'Monitoreo soberano', p: ['Detectar extracción ilegal o invasión', 'Re-puntuación trimestral de activos', 'Alertas de drift, fugas y empoderamiento'] },
      ],
    },
    stats: { labels: ['km² escaneados', 'Países cubiertos', 'Entrega media', 'Capital sin riesgo'] },
    services: {
      title: 'Servicios de ', titleAccent: 'inteligencia estratégica',
      sub: 'Compromisos por km² cuando necesita respuestas decisivas sobre un perímetro definido, rápido. Todos los precios desde — alcance por proyecto.',
      from: 'Desde',
      cards: [
        { t: 'Auditoría Hidro', d: 'Asegurar licencia social mapeando el agua antes de extraer.', delivery: '10–14 días', rigor: 'Táctico', value: 'Riesgo · Licencia social', price: '€500 / km²', cta: 'Definir Auditoría →' },
        { t: 'Scan multicapa', d: 'Fusión hiperespectral + SAR sobre firmas oro, litio, cobre.', delivery: '15–25 días', rigor: 'Estratégico', value: 'Aceleración de descubrimiento', price: '€2 000 / km²', cta: 'Definir Scan →' },
        { t: 'Mining Intelligence avanzada', d: 'Vectorización de pegmatitas, convergencia estructural y ranking 3D.', delivery: '20–35 días', rigor: 'Premium', value: 'Optimización CAPEX', price: '€5 000 / km²', cta: 'Definir Mining →' },
        { t: 'Monitoreo continuo', d: 'Re-puntuación trimestral — drift, fugas, invasión.', delivery: 'Ciclo 90 d', rigor: 'Continuo', value: 'Protección de activos', price: '€120k / año', cta: 'Definir Monitoreo →' },
        { t: 'Fusión de datos privada', d: 'Integre sus estudios y logs históricos en un núcleo ML soberano.', delivery: 'A demanda', rigor: 'Soberano', value: 'Apalancamiento IP soberano', price: '€50k / proyecto', cta: 'Definir Fusión →' },
      ],
      ref: 'Una sola campaña de perforación seca evitada financia típicamente 6–10 de los escaneos más extensos.',
    },
    pricing: {
      title: 'Acceso a ', titleAccent: 'la plataforma',
      sub: 'Suscripciones calibradas al capital en riesgo. Cada tier comparado con el costo de una sola campaña fallida.',
      popular: 'Más popular',
      from: 'Desde',
      cards: [
        { t: 'STARTUP EXPLORATION', d: 'Juniors · exploradores tempranos', price: '€48k / año', note: '≈ 1/10 de una sola perforación seca', f: ['Hasta 500 km²', '3 capas de commodities', 'Screening pre-permiso', 'Auditorías hídricas y sociales', 'Evitar 1 pozo seco → ROI > 8×'], cta: 'Solicitar acceso →' },
        { t: 'OPERADOR REGIONAL', d: 'Productores mid-tier · concesiones regionales', price: '€220k / año', note: 'Se paga con 1 campaña evitada', f: ['Hasta 5 000 km²', 'Todas las commodities', 'Acceso API', 'Reporte descubrimiento inversor', 'Monitoreo y alertas', '−68% CAPEX típico'], cta: 'Solicitar acceso →' },
        { t: 'GRUPO MINERO GLOBAL', d: 'Majors · portafolios multi-jurisdicción', price: '€950k / año', note: 'vs €5–15M / campaña fallida', f: ['km² ilimitados', 'Fine-tuning modelo privado', 'Re-puntuación trimestral', 'Re-clasificación portafolio global', 'Due diligence M&A', 'Realoca CAPEX 9 cifras'], cta: 'Reservar briefing →' },
        { t: 'GOBIERNO / SOBERANO', d: 'Ministerios · fondos soberanos · defensa', price: '€2,5M+ / engagement', note: 'Censo nacional', f: ['Cobertura nacional', 'Nube soberana · IP completa', 'Prioridad de perforación por concesión', 'Overlays geopolíticos', 'Censo de minerales críticos', 'Mapeo seguridad hídrica'], cta: 'Solicitar briefing soberano →' },
      ],
      footer: 'Una sola campaña seca evitada puede financiar toda la plataforma — durante años.',
    },
    form: {
      received: '¡Briefing recibido!',
      receivedDesc: 'Nuestro equipo le devolverá inteligencia puntuada, clasificada y drill-ready en semanas. Confirmación enviada a',
      title: 'Solicitar briefing soberano',
      sub: 'Informe a SOCRAT sobre su concesión, cuenca o perímetro soberano. Confidencial. NDA disponible.',
      step1: 'Paso 1 — Perímetro y Contacto', step2: 'Paso 2 — Misión y Contexto',
      fullName: 'Nombre completo', company: 'Empresa', email: 'Email profesional', phone: 'Teléfono / WhatsApp',
      country: 'País', selectCountry: 'Seleccionar país...',
      zoneLoc: 'Descripción del perímetro', zoneLocPh: 'ej. Norte de Mali, 20km al este de Kidal — o pegue coordenadas GPS',
      zoneSize: 'Tamaño aproximado',
      next: 'Siguiente', back: '← Volver',
      analysisType: 'Tipo de misión',
      analysisTypes: ['Oro · Cobre · Hierro', 'Litio y Tierras raras', 'Petróleo y Gas', 'Hidro y Seguridad del agua', 'Inteligencia agrícola', 'Monitoreo soberano'],
      reportType: 'Nivel de compromiso',
      reportTypes: ['Auditoría Hidro (desde €500/km²)', 'Scan multicapa (desde €2 000/km²)', 'Mining Intelligence (desde €5 000/km²)', 'Monitoreo continuo (desde €120k/año)', 'Acceso plataforma (desde €48k/año)', 'Briefing soberano'],
      urgency: 'Urgencia del proyecto',
      urgencyOpts: { standard: 'Estándar', urgent: 'Urgente (< 48H)', critical: 'Crítico — Decisión inminente' },
      context: 'Contexto adicional',
      nda: 'Sí, requiero un acuerdo de confidencialidad (NDA)',
      found: '¿Cómo nos encontró?', selectOpt: 'Seleccionar...',
      submit: 'Enviar briefing →', submitting: 'Enviando...',
    },
    faq: {
      title: 'Preguntas ', titleAccent: 'frecuentes',
      items: [
        { q: '¿Qué hace diferente a SOCRAT?', a: 'SOCRAT no es un revendedor de imágenes ni un dashboard GIS — es el único agente IA autónomo dedicado al descubrimiento de recursos naturales. Tiene acceso en vivo a constelaciones multiespectrales, SAR, hiperespectrales y térmicas, IoT terrestre y sensores geofísicos, e ingiere sus logs históricos en un núcleo ML soberano.' },
        { q: '¿Qué tan precisos son los reportes de SOCRAT?', a: 'Confianza 85–95% según commodity y zona. Multiespectral hasta 30 cm + SAR, térmico, magnetometría, gravedad y radiometría, validado contra datos de perforación históricos.' },
        { q: '¿Reemplaza el análisis satelital los estudios de campo?', a: 'No — SOCRAT es un multiplicador de fuerza. Pre-puntuando cada km² desde el espacio e IoT, enfoca estudios costosos y perforaciones solo en los blancos de mayor probabilidad, reduciendo CAPEX 60–80%.' },
        { q: '¿Qué recursos detecta?', a: 'Oro, cobre, hierro, zinc, litio, tierras raras, cobalto, níquel, filtraciones de petróleo/gas, agua subterránea, composición del suelo, salud de cultivos.' },
        { q: '¿Mis datos son confidenciales?', a: 'Absolutamente. Datos cifrados en tránsito y en reposo. NDA por defecto desde Scan multicapa. Tier soberano alojado en jurisdicción con transferencia IP completa.' },
        { q: '¿Cubren todos los países?', a: 'Sí — SOCRAT opera globalmente. De screening 50 km² a censo nacional de 5 000+ km², escalamos al perímetro.' },
      ],
    },
    footer: { tagline: 'Deje de financiar perforaciones. Financie respuestas.', solutions: 'Soluciones', company: 'Empresa', contact: 'Contacto', rights: '© 2026 Sovereign Scout · Quantumbox DeepTech Lab. Todos los derechos reservados.' },
  },
} as const;

// ─── ANIMATED COUNTER ───
const AnimatedCounter = ({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const duration = 2000;
        const startTime = Date.now();
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * end));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);
  return <div ref={ref} className="text-4xl md:text-5xl font-bold text-sovereign-gold">{prefix}{count.toLocaleString()}{suffix}</div>;
};

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-sovereign-navy/10">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left gap-4">
        <span className="font-semibold text-sovereign-navy text-lg">{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-sovereign-gold flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-sovereign-gold flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="pb-5 text-sovereign-navy/80 leading-relaxed text-base">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  pickaxe: Pickaxe, cpu: Cpu, fuel: Fuel, tree: TreePine, wheat: Wheat, shield: Shield,
};

const UseCaseCard = ({ icon, title, points }: { icon: string; title: string; points: string[] }) => {
  const Icon = ICON_MAP[icon] || Layers;
  return (
    <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-sovereign-navy/5 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-sovereign-gold/5 rounded-full -mr-12 -mt-12 group-hover:bg-sovereign-gold/15 transition-colors" />
      <div className="relative">
        <div className="w-14 h-14 rounded-xl bg-sovereign-navy/95 flex items-center justify-center mb-5 shadow-md">
          <Icon className="h-7 w-7 text-sovereign-gold" />
        </div>
        <h3 className="text-lg font-bold text-sovereign-navy mb-4 tracking-tight">{title}</h3>
        <ul className="space-y-2">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sovereign-navy/80 text-sm leading-relaxed">
              <Check className="h-4 w-4 text-sovereign-gold mt-0.5 flex-shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ServiceCard = ({ title, desc, delivery, rigor, value, price, cta, fromLabel }: { title: string; desc: string; delivery: string; rigor: string; value: string; price: string; cta: string; fromLabel: string }) => (
  <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-xl border border-sovereign-navy/10 transition-all hover:-translate-y-1 flex flex-col">
    <div className="text-[10px] font-mono tracking-[0.2em] text-sovereign-gold mb-2">TIER · {rigor.toUpperCase()}</div>
    <h3 className="text-xl font-bold text-sovereign-navy mb-2 tracking-tight">{title}</h3>
    <p className="text-sm text-sovereign-navy/75 mb-5 leading-relaxed flex-grow">{desc}</p>
    <div className="grid grid-cols-2 gap-3 mb-5 text-xs">
      <div className="border-l-2 border-sovereign-gold pl-2"><div className="text-sovereign-navy/50 uppercase tracking-wider text-[10px] font-semibold">Delivery</div><div className="text-sovereign-navy font-semibold">{delivery}</div></div>
      <div className="border-l-2 border-sovereign-gold pl-2"><div className="text-sovereign-navy/50 uppercase tracking-wider text-[10px] font-semibold">Value</div><div className="text-sovereign-navy font-semibold">{value}</div></div>
    </div>
    <div className="border-t border-sovereign-navy/10 pt-4 mb-4">
      <div className="text-[10px] font-mono tracking-widest text-sovereign-navy/50 mb-1">{fromLabel.toUpperCase()}</div>
      <div className="text-2xl font-extrabold text-sovereign-navy">{price}</div>
    </div>
    <a href="#analyze-my-zone" className="block text-center py-3 px-4 rounded-lg font-semibold text-sm border-2 border-sovereign-navy text-sovereign-navy hover:bg-sovereign-navy hover:text-sovereign-gold transition-all">{cta}</a>
  </div>
);

const PricingCard = ({ title, desc, features, cta, price, note, highlighted = false, popularLabel, fromLabel }: { title: string; desc: string; features: string[]; cta: string; price: string; note: string; highlighted?: boolean; popularLabel: string; fromLabel: string }) => (
  <div className={`rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 flex flex-col ${highlighted ? 'bg-sovereign-navy text-white ring-2 ring-sovereign-gold shadow-2xl relative' : 'bg-white border border-sovereign-navy/10 shadow-sm hover:shadow-xl'}`}>
    {highlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sovereign-gold text-sovereign-navy text-[10px] font-bold tracking-widest px-3 py-1 rounded-full">{popularLabel}</div>}
    <div className="text-[10px] font-mono tracking-[0.2em] mb-2 text-sovereign-gold">{title}</div>
    <p className={`text-sm mb-5 leading-relaxed ${highlighted ? 'text-white/80' : 'text-sovereign-navy/70'}`}>{desc}</p>
    <div className={`border-t pt-4 mb-5 ${highlighted ? 'border-white/15' : 'border-sovereign-navy/10'}`}>
      <div className={`text-[10px] font-mono tracking-widest mb-1 ${highlighted ? 'text-white/50' : 'text-sovereign-navy/50'}`}>{fromLabel.toUpperCase()}</div>
      <div className={`text-2xl font-extrabold ${highlighted ? 'text-white' : 'text-sovereign-navy'}`}>{price}</div>
      <div className={`text-xs mt-1 ${highlighted ? 'text-sovereign-gold' : 'text-sovereign-navy/60'}`}>{note}</div>
    </div>
    <ul className="space-y-2.5 mb-6 flex-grow">
      {features.map((f, i) => (
        <li key={i} className={`flex items-start gap-2 text-sm leading-relaxed ${highlighted ? 'text-white/90' : 'text-sovereign-navy/85'}`}>
          <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-sovereign-gold" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <a href="#analyze-my-zone" className={`block text-center py-3 px-4 rounded-lg font-semibold text-sm transition-all ${highlighted ? 'bg-sovereign-gold text-sovereign-navy hover:bg-sovereign-gold/90' : 'border-2 border-sovereign-navy text-sovereign-navy hover:bg-sovereign-navy hover:text-sovereign-gold'}`}>
      {cta}
    </a>
  </div>
);

// ═══════════════════════════════════════════
const SovereignScout = () => {
  useSEO({
    title: 'SOCRAT — AI Agent for Mineral Exploration | Satellite + IoT + Geophysics | Sovereign Scout',
    description: 'SOCRAT is the only autonomous AI agent for natural-resource discovery — fusing multi-spectral & SAR satellites, IoT sensors and geophysical surveys. Pre-CAPEX km² scoring for gold, copper, lithium, rare earths, oil & gas. Pricing from €500/km² · €48k/year.',
    keywords: 'mineral exploration AI, satellite mineral exploration, SOCRAT AI agent, natural resource exploration platform, mining intelligence software, lithium exploration satellite, rare earth exploration AI, gold copper exploration, geophysical fusion AI, hyperspectral mineral detection, SAR mining, oil gas seepage detection, sovereign mineral intelligence, drill-target ranking AI, junior mining technology, mid-tier mining intelligence, mining majors AI platform, agent IA exploration minière, exploration satellitaire, intelligence minière souveraine',
    canonical: 'https://quantumbox.ai/sovereign-scout',
    ogTitle: 'SOCRAT — The only AI agent for natural-resource discovery',
    ogDescription: 'Satellite + IoT + geophysical fusion. Pre-CAPEX km² scoring for mining, oil & gas. Stop funding drills. Start funding answers.',
  });

  // Inject JSON-LD structured data targeting exploration companies
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = localStorage.getItem('sovereign_lang') as Lang | null;
    if (saved && ['en','fr','es'].includes(saved)) return saved;
    const nav = navigator.language.slice(0, 2);
    return (['en','fr','es'].includes(nav) ? nav : 'en') as Lang;
  });
  useEffect(() => { localStorage.setItem('sovereign_lang', lang); }, [lang]);
  const t = T[lang];

  // JSON-LD structured data targeting exploration companies
  useEffect(() => {
    const orgId = 'https://quantumbox.ai/#organization';
    const pageUrl = 'https://quantumbox.ai/sovereign-scout';

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': orgId,
          name: 'Quantumbox DeepTech Lab',
          alternateName: 'QuantumBox AI',
          url: 'https://quantumbox.ai',
          logo: {
            '@type': 'ImageObject',
            url: 'https://quantumbox.ai/lovable-uploads/13ccca33-efca-4b7f-9ef1-50aafecd8c64.png',
            width: 512,
            height: 512,
          },
          image: 'https://quantumbox.ai/lovable-uploads/13ccca33-efca-4b7f-9ef1-50aafecd8c64.png',
          sameAs: [
            'https://www.linkedin.com/company/quantumbox-ia/',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'admin@quantumbox.ai',
            areaServed: 'Worldwide',
            availableLanguage: ['English', 'French', 'Spanish'],
          },
          knowsAbout: [
            'Mineral Exploration AI',
            'Satellite Remote Sensing',
            'Geophysical Data Fusion',
            'Natural Resource Discovery',
            'Autonomous AI Agents',
            'Hyperspectral Imaging',
            'SAR Satellite Analytics',
            'IoT Sensor Networks',
          ],
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${pageUrl}#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://quantumbox.ai',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'SOCRAT — Sovereign Scout',
              item: pageUrl,
            },
          ],
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${pageUrl}#software`,
          name: 'SOCRAT — Sovereign Resource Intelligence Agent',
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: 'GeospatialAnalysisSoftware',
          operatingSystem: 'Web',
          url: pageUrl,
          description: 'Autonomous AI agent for natural-resource discovery. Fuses multi-spectral, SAR, hyperspectral and thermal satellite data with IoT sensors, geophysical surveys and historic drill logs to deliver pre-CAPEX km² scoring for mining, oil & gas, lithium and rare-earth exploration.',
          inLanguage: ['en', 'fr', 'es'],
          provider: { '@id': orgId },
          offers: [
            { '@type': 'Offer', name: 'Hydro Audit', price: '500', priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: pageUrl },
            { '@type': 'Offer', name: 'Multi-Layer Resource Scan', price: '2000', priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: pageUrl },
            { '@type': 'Offer', name: 'Advanced Mining Intelligence', price: '5000', priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: pageUrl },
            { '@type': 'Offer', name: 'Startup Exploration Platform', price: '48000', priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: pageUrl },
            { '@type': 'Offer', name: 'Sovereign Engagement', price: '2500000', priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: pageUrl },
          ],
          audience: { '@type': 'Audience', audienceType: 'Mining exploration companies, junior miners, mid-tier producers, mining majors, oil & gas operators, ministries of mines, sovereign wealth funds' },
          featureList: ['Multi-spectral & SAR satellite fusion', 'IoT and geophysical sensor ingestion', 'Pre-CAPEX km² probability scoring', 'Sovereign ML core', 'Drill-target 3D ranking', 'Hydro & social licence audits'],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '48',
            bestRating: '5',
            worstRating: '1',
          },
        },
        {
          '@type': 'FAQPage',
          '@id': `${pageUrl}#faq`,
          mainEntity: t.faq.items.map(it => ({
            '@type': 'Question',
            name: it.q,
            acceptedAnswer: { '@type': 'Answer', text: it.a },
          })),
        },
      ],
    };
    const id = 'socrat-jsonld';
    document.getElementById(id)?.remove();
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { document.getElementById(id)?.remove(); };
  }, [t]);



  const [mobileNav, setMobileNav] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [bookDemoOpen, setBookDemoOpen] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formEmail, setFormEmail] = useState('');

  const [form, setForm] = useState({
    full_name: '', company: '', email: '', phone: '', country: '',
    zone_location: '', zone_size: '',
    analysis_types: [] as string[], report_type: '', project_urgency: 'standard',
    additional_context: '', nda_required: false, how_found_us: ''
  });

  const updateField = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
  const toggleAnalysisType = (type: string) => {
    setForm(f => ({ ...f, analysis_types: f.analysis_types.includes(type) ? f.analysis_types.filter(x => x !== type) : [...f.analysis_types, type] }));
  };

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileNav(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.from('zone_analysis_requests').insert({
        full_name: form.full_name, company: form.company || null, email: form.email,
        phone: form.phone || null, country: form.country, zone_location: form.zone_location,
        zone_size: form.zone_size, analysis_types: form.analysis_types, report_type: form.report_type,
        project_urgency: form.project_urgency, additional_context: form.additional_context || null,
        nda_required: form.nda_required, how_found_us: form.how_found_us || null
      });
      if (error) throw error;
      const { notifyFormSubmission } = await import('@/lib/notifyForm');
      notifyFormSubmission('Sovereign Scout — Zone Analysis Request', form as any);
      setFormEmail(form.email); setSubmitted(true);
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
    } catch (err) { console.error('Submission error:', err); }
    finally { setSubmitting(false); }
  };

  const navLinks = [
    { label: t.nav.solutions, id: 'solutions' },
    { label: t.nav.how, id: 'how-it-works' },
    { label: t.nav.cases, id: 'use-cases' },
    { label: t.nav.pricing, id: 'pricing' },
    { label: t.nav.contact, id: 'analyze-my-zone' },
  ];

  const step1Valid = form.full_name && form.email && form.country && form.zone_location && form.zone_size;
  const step2Valid = form.analysis_types.length > 0 && form.report_type;

  const LangSwitcher = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex items-center gap-1 ${mobile ? 'justify-center pt-2' : ''}`}>
      {(['en','fr','es'] as Lang[]).map(l => (
        <button key={l} onClick={() => setLang(l)} className={`text-xs font-bold px-2 py-1 rounded transition-colors ${lang === l ? 'bg-sovereign-navy text-sovereign-gold' : 'text-sovereign-navy/50 hover:text-sovereign-navy'}`} aria-label={`Switch to ${l.toUpperCase()}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }} className="text-base">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16 gap-4">
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-2 flex-shrink-0">
            <Satellite className="h-6 w-6 text-sovereign-navy" />
            <span className="text-base lg:text-lg font-extrabold tracking-widest text-sovereign-navy">SOVEREIGN SCOUT</span>
          </button>
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-semibold text-sovereign-navy/80 hover:text-sovereign-navy transition-colors">{l.label}</button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <LangSwitcher />
            <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-sovereign-navy/90 transition-colors border-2 border-sovereign-gold">
              {t.nav.cta}
            </button>
          </div>
          <button className="md:hidden text-sovereign-navy" onClick={() => setMobileNav(!mobileNav)} aria-label="menu">{mobileNav ? <X /> : <Menu />}</button>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-white border-t px-4 py-4 space-y-3">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left text-sovereign-navy font-semibold py-2">{l.label}</button>
            ))}
            <LangSwitcher mobile />
            <button onClick={() => scrollTo('analyze-my-zone')} className="w-full bg-sovereign-navy text-white py-3 rounded-lg font-semibold border-2 border-sovereign-gold">{t.nav.cta}</button>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="hero" className="relative min-h-screen flex items-center pt-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #0f2847 50%, #1B3A6B 100%)' }}>
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1.5" fill="#C9A84C" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        <div className="absolute top-20 right-10 md:right-20 w-48 h-48 md:w-72 md:h-72 opacity-20">
          <div className="w-full h-full rounded-full border-2 border-sovereign-gold animate-spin" style={{ animationDuration: '20s' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-sovereign-gold rounded-full" />
          </div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              {t.hero.h1a}<span className="text-sovereign-gold">{t.hero.h1b}</span>
            </h1>
            <p className="text-lg md:text-xl text-white mb-4 font-medium">
              {t.hero.sub1a}<span className="text-sovereign-gold">{t.hero.sub1b}</span>
            </p>
            <p className="text-white/85 mb-8 max-w-2xl leading-relaxed text-base md:text-lg">
              {t.hero.sub2}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all flex items-center justify-center gap-2">
                {t.hero.cta1} <ArrowRight className="h-5 w-5" />
              </button>
              <button onClick={() => setSampleOpen(true)} className="border-2 border-sovereign-gold text-sovereign-gold bg-white/5 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-sovereign-gold hover:text-sovereign-navy transition-all flex items-center justify-center gap-2">
                📄 {t.hero.cta2}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: <Satellite className="h-6 w-6 text-sovereign-gold" />, text: t.hero.b1 },
                { icon: <Zap className="h-6 w-6 text-sovereign-gold" />, text: t.hero.b2 },
                { icon: <Clock className="h-6 w-6 text-sovereign-gold" />, text: t.hero.b3 }
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-lg px-4 py-3">
                  {b.icon}
                  <span className="text-white text-sm font-medium">{b.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* LIVE DEMO VIDEO — analyzed once by Gemini, replayed with overlay */}
      <section id="live-demo" className="relative py-16 md:py-24 overflow-hidden" style={{ background: 'linear-gradient(180deg, #1B3A6B 0%, #0f2847 100%)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-3">
              {lang === 'fr' ? 'Voir SOCRAT en action' : lang === 'es' ? 'Ver SOCRAT en acción' : 'See SOCRAT in action'}
            </h2>
            <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {lang === 'fr'
                ? 'Scan satellite en direct, fusion multi-source et détection IA d\'or et d\'eau — analysé une fois par Gemini.'
                : lang === 'es'
                ? 'Escaneo satelital en vivo, fusión multifuente y detección IA de oro y agua — analizado una vez por Gemini.'
                : 'Live satellite scanning, multi-source fusion and AI detection of gold & water — analyzed once by Gemini.'}
            </p>
          </div>
          <SocratDemoVideo lang={lang} onBookDemo={() => setBookDemoOpen(true)} />
        </div>
      </section>

      {/* SOCRAT SPOTLIGHT */}
      <section id="socrat" className="relative py-20 md:py-28 overflow-hidden" style={{ background: 'linear-gradient(180deg, #0f2847 0%, #1B3A6B 100%)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-block text-[11px] font-mono tracking-[0.3em] text-sovereign-gold border border-sovereign-gold/40 rounded-full px-4 py-1.5 mb-5">{t.socrat.tag}</div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight max-w-4xl mx-auto">{t.socrat.title}</h2>
            <p className="text-white/80 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">{t.socrat.sub}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
            {t.socrat.stats.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-sovereign-gold/20 rounded-2xl p-5 text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-sovereign-gold mb-1">{s.v}</div>
                <div className="text-white/70 text-xs md:text-sm uppercase tracking-wider">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.socrat.caps.map((c, i) => {
              const Icon = [Satellite, Radio, Radar, Database][i];
              return (
                <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:border-sovereign-gold/40 transition-colors">
                  <div className="w-11 h-11 rounded-lg bg-sovereign-gold/15 border border-sovereign-gold/30 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-sovereign-gold" />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{c.t}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{c.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="solutions" className="py-20 md:py-28" style={{ background: '#F4F6FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16 tracking-tight">
            {t.why.title}<span className="text-sovereign-gold">{t.why.titleAccent}</span>{t.why.q}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-sovereign-navy/5">
              <div className="text-[10px] font-mono tracking-[0.2em] text-red-500 mb-2">BEFORE</div>
              <h3 className="text-xl font-bold text-sovereign-navy mb-6">✕ {t.why.old}</h3>
              <ul className="space-y-4">
                {t.why.olds.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sovereign-navy/85 text-base">
                    <X className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" /><span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-md border-2 border-sovereign-gold/40 relative">
              <div className="absolute -top-3 left-8 bg-sovereign-gold text-sovereign-navy text-[10px] font-bold tracking-widest px-3 py-1 rounded-full">SOCRAT</div>
              <div className="text-[10px] font-mono tracking-[0.2em] text-sovereign-gold mb-2">AFTER</div>
              <h3 className="text-xl font-bold text-sovereign-navy mb-6">✓ {t.why.new}</h3>
              <ul className="space-y-4">
                {t.why.news.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sovereign-navy text-base">
                    <Check className="h-5 w-5 text-sovereign-gold mt-0.5 flex-shrink-0" /><span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16 tracking-tight">
            {t.how.title}<span className="text-sovereign-gold">{t.how.titleAccent}</span>{t.how.t2}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-sovereign-gold/30" />
            {t.how.steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="text-center relative z-10">
                <div className="w-20 h-20 mx-auto bg-sovereign-navy rounded-2xl flex items-center justify-center text-sovereign-gold mb-6 shadow-lg ring-4 ring-white">
                  {[<Send className="h-8 w-8" key="a" />, <Cpu className="h-8 w-8" key="b" />, <BarChart3 className="h-8 w-8" key="c" />][i]}
                </div>
                <div className="text-sovereign-gold font-mono text-[11px] tracking-[0.25em] mb-2">{t.how.step} 0{i + 1}</div>
                <h3 className="text-xl font-bold text-sovereign-navy mb-3 tracking-tight">{s.t}</h3>
                <p className="text-sovereign-navy/75 max-w-xs mx-auto text-base leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-14">
            <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all inline-flex items-center gap-2">
              {t.how.cta} <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="py-20 md:py-28" style={{ background: '#F4F6FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-4 tracking-tight">
            {t.cases.title}<span className="text-sovereign-gold">{t.cases.titleAccent}</span>
          </h2>
          <p className="text-center text-sovereign-navy/75 mb-16 max-w-2xl mx-auto text-base md:text-lg">{t.cases.sub}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.cases.items.map((c, i) => <UseCaseCard key={i} icon={c.i} title={c.t} points={[...c.p]} />)}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 md:py-20" style={{ background: 'linear-gradient(135deg, #1B3A6B, #0f2847)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { end: 12400, suffix: '+', Icon: Satellite },
              { end: 47, suffix: '', Icon: Globe },
              { end: 14, suffix: 'd', Icon: Clock },
              { end: 70, suffix: '%', Icon: Activity },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <s.Icon className="h-7 w-7 text-sovereign-gold mx-auto mb-2" />
                <AnimatedCounter end={s.end} suffix={s.suffix} />
                <div className="text-white/70 text-xs md:text-sm mt-2 font-medium uppercase tracking-wider">{t.stats.labels[i]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — per km² */}
      <section id="services" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-block text-[11px] font-mono tracking-[0.3em] text-sovereign-gold border border-sovereign-gold/40 rounded-full px-4 py-1.5 mb-4">03 · SERVICES</div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy mb-4 tracking-tight">
              {t.services.title}<span className="text-sovereign-gold">{t.services.titleAccent}</span>
            </h2>
            <p className="text-sovereign-navy/75 max-w-3xl mx-auto text-base md:text-lg">{t.services.sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {t.services.cards.map((c, i) => (
              <ServiceCard key={i} title={c.t} desc={c.d} delivery={c.delivery} rigor={c.rigor} value={c.value} price={c.price} cta={c.cta} fromLabel={t.services.from} />
            ))}
          </div>
          <p className="text-center text-sovereign-navy/60 italic mt-10 text-sm max-w-3xl mx-auto">{t.services.ref}</p>
        </div>
      </section>

      {/* PRICING — Platform tiers */}
      <section id="pricing" className="py-20 md:py-28" style={{ background: '#F4F6FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-block text-[11px] font-mono tracking-[0.3em] text-sovereign-gold border border-sovereign-gold/40 rounded-full px-4 py-1.5 mb-4">04 · PLATFORM</div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy mb-4 tracking-tight">
              {t.pricing.title}<span className="text-sovereign-gold">{t.pricing.titleAccent}</span>
            </h2>
            <p className="text-sovereign-navy/75 max-w-3xl mx-auto text-base md:text-lg">{t.pricing.sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.pricing.cards.map((c, i) => (
              <PricingCard key={i} title={c.t} desc={c.d} features={[...c.f]} cta={c.cta} price={c.price} note={c.note} highlighted={i === 1} popularLabel={t.pricing.popular} fromLabel={t.pricing.from} />
            ))}
          </div>
          <p className="text-center text-sovereign-navy font-semibold mt-10 text-base md:text-lg max-w-3xl mx-auto">{t.pricing.footer}</p>
        </div>
      </section>

      <section id="analyze-my-zone" className="py-20 md:py-28" style={{ background: 'linear-gradient(135deg, #1B3A6B, #0f2847)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
            {submitted ? (
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-sovereign-navy mb-4">{t.form.received}</h3>
                <p className="text-sovereign-navy/80 max-w-md mx-auto text-base">{t.form.receivedDesc} <strong>{formEmail}</strong>.</p>
              </motion.div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-sovereign-navy mb-2">{t.form.title}</h2>
                  <p className="text-sovereign-navy/75 text-base">{t.form.sub}</p>
                </div>
                <div className="flex gap-2 mb-8">
                  <div className={`h-1.5 rounded-full flex-1 transition-colors ${formStep >= 1 ? 'bg-sovereign-gold' : 'bg-gray-200'}`} />
                  <div className={`h-1.5 rounded-full flex-1 transition-colors ${formStep >= 2 ? 'bg-sovereign-gold' : 'bg-gray-200'}`} />
                </div>
                <form onSubmit={handleSubmit}>
                  {formStep === 1 && (
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                      <h3 className="font-bold text-sovereign-navy text-sm uppercase tracking-wider mb-4">{t.form.step1}</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.fullName} *</label>
                          <input type="text" required value={form.full_name} onChange={e => updateField('full_name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.company}</label>
                          <input type="text" value={form.company} onChange={e => updateField('company', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.email} *</label>
                          <input type="email" required value={form.email} onChange={e => updateField('email', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.phone}</label>
                          <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.country} *</label>
                        <select required value={form.country} onChange={e => updateField('country', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="">{t.form.selectCountry}</option>
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.zoneLoc} *</label>
                        <textarea required value={form.zone_location} onChange={e => updateField('zone_location', e.target.value)} placeholder={t.form.zoneLocPh} rows={3} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all resize-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-2">{t.form.zoneSize} *</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {['< 50 km²', '50–500 km²', '500–5,000 km²', '> 5,000 km²'].map(size => (
                            <label key={size} className={`cursor-pointer border-2 rounded-lg px-3 py-2.5 text-center text-sm font-medium transition-all ${form.zone_size === size ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy' : 'border-gray-200 text-sovereign-navy/80 hover:border-sovereign-gold/50'}`}>
                              <input type="radio" name="zone_size" value={size} checked={form.zone_size === size} onChange={e => updateField('zone_size', e.target.value)} className="sr-only" />
                              {size}
                            </label>
                          ))}
                        </div>
                      </div>
                      <button type="button" disabled={!step1Valid} onClick={() => setFormStep(2)} className="w-full bg-sovereign-navy text-white py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                        {t.form.next} <ArrowRight className="h-5 w-5" />
                      </button>
                    </motion.div>
                  )}
                  {formStep === 2 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                      <h3 className="font-bold text-sovereign-navy text-sm uppercase tracking-wider mb-4">{t.form.step2}</h3>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-2">{t.form.analysisType} *</label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {t.form.analysisTypes.map(type => (
                            <label key={type} className={`cursor-pointer flex items-center gap-2 border-2 rounded-lg px-3 py-2.5 text-sm transition-all ${form.analysis_types.includes(type) ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy font-medium' : 'border-gray-200 text-sovereign-navy/80 hover:border-sovereign-gold/50'}`}>
                              <input type="checkbox" checked={form.analysis_types.includes(type)} onChange={() => toggleAnalysisType(type)} className="sr-only" />
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${form.analysis_types.includes(type) ? 'border-sovereign-gold bg-sovereign-gold' : 'border-gray-300'}`}>
                                {form.analysis_types.includes(type) && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {type}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-2">{t.form.reportType} *</label>
                        <div className="space-y-2">
                          {t.form.reportTypes.map(opt => (
                            <label key={opt} className={`cursor-pointer flex items-center gap-3 border-2 rounded-lg px-4 py-3 text-sm transition-all ${form.report_type === opt ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy font-medium' : 'border-gray-200 text-sovereign-navy/80 hover:border-sovereign-gold/50'}`}>
                              <input type="radio" name="report_type" value={opt} checked={form.report_type === opt} onChange={e => updateField('report_type', e.target.value)} className="sr-only" />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${form.report_type === opt ? 'border-sovereign-gold' : 'border-gray-300'}`}>
                                {form.report_type === opt && <div className="w-2 h-2 rounded-full bg-sovereign-gold" />}
                              </div>
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.urgency}</label>
                        <select value={form.project_urgency} onChange={e => updateField('project_urgency', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="standard">{t.form.urgencyOpts.standard}</option>
                          <option value="urgent">{t.form.urgencyOpts.urgent}</option>
                          <option value="critical">{t.form.urgencyOpts.critical}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.context}</label>
                        <textarea value={form.additional_context} onChange={e => updateField('additional_context', e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all resize-none" />
                      </div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.nda_required} onChange={e => updateField('nda_required', e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-sovereign-gold focus:ring-sovereign-gold" />
                        <span className="text-sm text-sovereign-navy">{t.form.nda}</span>
                      </label>
                      <div>
                        <label className="block text-sm font-semibold text-sovereign-navy mb-1">{t.form.found}</label>
                        <select value={form.how_found_us} onChange={e => updateField('how_found_us', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="">{t.form.selectOpt}</option>
                          <option value="Google">Google</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Referral">Referral</option>
                          <option value="Conference">Conference</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="flex gap-4">
                        <button type="button" onClick={() => setFormStep(1)} className="flex-1 border-2 border-sovereign-navy text-sovereign-navy py-4 rounded-lg font-semibold hover:bg-sovereign-navy/5 transition-all">{t.form.back}</button>
                        <button type="submit" disabled={!step2Valid || submitting} className="flex-1 bg-sovereign-navy text-white py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                          {submitting ? t.form.submitting : t.form.submit}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16">
            {t.faq.title}<span className="text-sovereign-gold">{t.faq.titleAccent}</span>
          </h2>
          <div>{t.faq.items.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}</div>
        </div>
      </section>

      {/* FOOTER */}
      {/* Resources strip — internal links to SEO landing pages */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-sm font-bold uppercase tracking-widest text-sovereign-navy/60 mb-6">Explore SOCRAT</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <a href="/sovereign-scout/mineral-exploration-ai-guide" className="block p-5 rounded-xl border-2 border-sovereign-navy/10 hover:border-sovereign-gold transition-colors group">
              <div className="text-xs font-bold text-sovereign-gold mb-2">PILLAR GUIDE</div>
              <div className="font-bold text-sovereign-navy group-hover:underline">Mineral Exploration AI — 2026 Buyer's Guide</div>
              <p className="text-sm text-sovereign-navy/70 mt-2">How AI fusion replaces speculative drilling. Vendor comparison. ROI math.</p>
            </a>
            <a href="/sovereign-scout/lithium-exploration-ai" className="block p-5 rounded-xl border-2 border-sovereign-navy/10 hover:border-sovereign-gold transition-colors group">
              <div className="text-xs font-bold text-sovereign-gold mb-2">CRITICAL MINERALS</div>
              <div className="font-bold text-sovereign-navy group-hover:underline">Lithium Exploration AI — Pegmatites & Brines from Space</div>
              <p className="text-sm text-sovereign-navy/70 mt-2">Hyperspectral pegmatite vectorisation across the Sahel, Australia & lithium triangle.</p>
            </a>
            <a href="/sovereign-scout/africa" className="block p-5 rounded-xl border-2 border-sovereign-navy/10 hover:border-sovereign-gold transition-colors group">
              <div className="text-xs font-bold text-sovereign-gold mb-2">REGIONAL · EN/FR</div>
              <div className="font-bold text-sovereign-navy group-hover:underline">Sovereign Mineral Exploration AI for Africa</div>
              <p className="text-sm text-sovereign-navy/70 mt-2">Built for ministries, juniors & majors. Bilingual. In-jurisdiction hosting.</p>
            </a>
          </div>
        </div>
      </section>

      <footer className="py-16" style={{ background: '#1B3A6B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Satellite className="h-6 w-6 text-sovereign-gold" />
                <span className="font-extrabold text-white tracking-widest">SOVEREIGN SCOUT</span>
              </div>
              <p className="text-white/70 text-sm">{t.footer.tagline}</p>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">{t.footer.solutions}</h4>
              <ul className="space-y-2 text-sm text-white/80">
                {t.cases.items.map((c, i) => <li key={i}><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">{c.t}</button></li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">{t.footer.company}</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><button onClick={() => scrollTo('how-it-works')} className="hover:text-white transition-colors">{t.nav.how}</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="hover:text-white transition-colors">{t.nav.pricing}</button></li>
                <li><button onClick={() => scrollTo('faq')} className="hover:text-white transition-colors">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">{t.footer.contact}</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> admin@quantumbox.ai</li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Paris, France</li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.linkedin.com/company/quantumbox-ia/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-sovereign-gold transition-colors"><Linkedin className="h-5 w-5" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-xs">{t.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
              <div className="border-b border-gray-200 pb-6 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Satellite className="h-5 w-5 text-sovereign-navy" />
                  <span className="font-extrabold text-sovereign-navy tracking-wider text-sm">SOVEREIGN SCOUT</span>
                </div>
                <h3 className="text-xl font-bold text-sovereign-navy">Intelligence Report — Zone Analysis</h3>
                <p className="text-sovereign-gold font-medium">West African Mining Corridor</p>
              </div>
              <div className="space-y-6">
                <p className="text-sovereign-navy/85 text-base">Multi-spectral analysis reveals three high-confidence mineralization zones within the target corridor, with gold and copper anomaly signatures...</p>
                <div className="bg-gray-100 rounded-lg h-40 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <span className="text-gray-400 font-bold tracking-widest">MAP CLASSIFIED</span>
                </div>
              </div>
              <div className="mt-8 text-center">
                <button onClick={() => { setShowModal(false); setTimeout(() => scrollTo('analyze-my-zone'), 300); }} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all inline-flex items-center gap-2">
                  {t.hero.cta1} <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BookDemoModal open={bookDemoOpen} onClose={() => setBookDemoOpen(false)} lang={lang} />
      <SampleReportModal open={sampleOpen} onClose={() => setSampleOpen(false)} lang={lang} />

      {/* Sticky bottom CTA bar — high-intent conversion lift */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-sovereign-navy/95 backdrop-blur-md border-t-2 border-sovereign-gold shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="hidden sm:block text-white text-sm font-medium">
              <span className="text-sovereign-gold font-bold">Confidential · NDA available</span> — brief SOCRAT on your concession or basin.
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setSampleOpen(true)}
                className="hidden md:inline-flex border-2 border-sovereign-gold/60 text-sovereign-gold px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-sovereign-gold hover:text-sovereign-navy transition-colors min-h-[44px] items-center gap-1.5"
              >
                📄 Sample report
              </button>
              <button
                onClick={() => setBookDemoOpen(true)}
                className="hidden sm:inline-flex border-2 border-white/30 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-white/10 transition-colors min-h-[44px] items-center"
              >
                Book live demo
              </button>
              <button
                onClick={() => scrollTo('analyze-my-zone')}
                className="bg-sovereign-gold text-sovereign-navy px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-yellow-300 transition-colors min-h-[44px] inline-flex items-center gap-2"
              >
                {t.nav.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SovereignScout;
